// Pure opstart-/migratieregels van de state-laag (port van legacy initGameOn, seedSampleLogs). Los van React zodat ze testbaar zijn.
import type { Activity, LogEntry } from '../domain/types';

/** Minimaal aantal activiteiten; daaronder wordt (zonder eigen dataset) opnieuw de standaardlijst geladen. */
export const MIN_DEFAULT_COUNT = 30;

/** Legacy-regel: leeg, of geen eigen dataset en minder dan 30 items → standaardlijst opnieuw laden. */
export function needsReseed(count: number, hasCustomDataset: boolean): boolean {
  return count === 0 || (!hasCustomDataset && count < MIN_DEFAULT_COUNT);
}

/** Eenmalige legacy-hernoeming 'Uit bouwen' → 'Hut bouwen'. */
export function applyLegacyRenames(list: Activity[]): { list: Activity[]; changed: boolean } {
  let changed = false;
  const next = list.map((a) => {
    if ((a.naam || '').trim().toLowerCase() === 'uit bouwen') {
      changed = true;
      return { ...a, naam: 'Hut bouwen', omschrijving: 'Bouw een gezellige hut van dekens of kussens' };
    }
    return a;
  });
  return { list: changed ? next : list, changed };
}

/** Hash-regel: een niet-aangepaste lijst wordt vernieuwd als de opgeslagen hash afwijkt van de huidige. */
export function defaultsOutdated(storedHash: string | null | undefined, currentHash: string, customized: boolean): boolean {
  return !customized && storedHash !== currentHash;
}

const nameKey = (naam: unknown) => String(naam ?? '').toLowerCase().trim();

/** Neemt scores over van `source` op naam (hoofdletterongevoelig). */
export function keepScoresByName<T extends { naam?: string; score?: number }>(target: T[], source: Activity[]): T[] {
  const scores = new Map(source.map((a) => [nameKey(a.naam), a.score || 0]));
  return target.map((t) => {
    const s = scores.get(nameKey(t.naam));
    return s !== undefined ? { ...t, score: s } : t;
  });
}

/** true als opschonen id's heeft gewijzigd (dan eenmalig terugschrijven, zoals legacy loadActivities). */
export function idsChanged(raw: readonly unknown[], normalized: readonly Activity[]): boolean {
  if (raw.length !== normalized.length) return true;
  return normalized.some((a, i) => {
    const r = raw[i] as { id?: unknown } | null;
    return !r || typeof r !== 'object' || r.id !== a.id;
  });
}

/** De 6 voorbeeldmissies van legacy seedSampleLogs, verdeeld over de 4 hoofdcategorieën. */
export function buildSampleLogs(now: number): LogEntry[] {
  const min = 60_000;
  const hour = 60 * min;
  const s = (offset: number, e: Omit<LogEntry, 'id' | 'moment'>, ago: number): LogEntry => ({
    id: `sample_${now - offset}`,
    ...e,
    moment: new Date(now - ago).toISOString(),
  });
  return [
    s(500, { activityId: 1, naam: 'Hutten bouwen in het bos', type: 'Buiten', duurMinuten: 60, energie: 'Veel energie', categorie: 'buiten-hoog' }, 45 * min),
    s(1000, { activityId: 14, naam: 'Stoepkrijten & hinkelen', type: 'Buiten', duurMinuten: 30, energie: 'Rustig', categorie: 'buiten-rustig' }, 180 * min),
    s(1500, { activityId: 12, naam: 'Verstoppertje in het donker', type: 'Binnen', duurMinuten: 35, energie: 'Veel energie', categorie: 'binnen-hoog' }, 24 * hour),
    s(2000, { activityId: 2, naam: 'Chocoladekoekjes bakken', type: 'Binnen', duurMinuten: 45, energie: 'Rustig', categorie: 'binnen-rustig' }, 28 * hour),
    s(2500, { activityId: 5, naam: 'Donald Duck pocket lezen', type: 'Binnen', duurMinuten: 30, energie: 'Rustig', categorie: 'binnen-rustig' }, 52 * hour),
    s(3000, { activityId: 4, naam: 'Skaten bij de skatebaan', type: 'Buiten', duurMinuten: 50, energie: 'Veel energie', categorie: 'buiten-hoog' }, 70 * hour),
  ];
}
